import { Trans } from '@lingui/react/macro';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { Button, Dialog } from '@utima/ui';
import { type ReactNode, useMemo, useState } from 'react';

type DiffViewMode = 'git' | 'raw';

type DiffLine = {
  type: 'context' | 'add' | 'del';
  oldLineNo?: number;
  newLineNo?: number;
  text: string;
};

type DiffHunk = {
  oldStart: number;
  oldCount: number;
  newStart: number;
  newCount: number;
  lines: DiffLine[];
};

type WordPart = {
  text: string;
  changed: boolean;
};

const DIFF_LOOKAHEAD = 40;
const DIFF_CONTEXT_LINES = 3;

function tokenizeWords(line: string): string[] {
  return line.split(/(\s+)/).filter(Boolean);
}

function buildLcsMatrix(a: string[], b: string[]): number[][] {
  const dp = Array.from({ length: a.length + 1 }, () =>
    Array.from<number>({ length: b.length + 1 }).fill(0),
  );

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  return dp;
}

function diffWords(
  oldLine: string,
  newLine: string,
): {
  removed: WordPart[];
  added: WordPart[];
} {
  const oldTokens = tokenizeWords(oldLine);
  const newTokens = tokenizeWords(newLine);
  const dp = buildLcsMatrix(oldTokens, newTokens);

  const removed: WordPart[] = [];
  const added: WordPart[] = [];

  let i = oldTokens.length;
  let j = newTokens.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      removed.unshift({ text: oldTokens[i - 1], changed: false });
      added.unshift({ text: newTokens[j - 1], changed: false });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      added.unshift({ text: newTokens[j - 1], changed: true });
      j--;
    } else if (i > 0) {
      removed.unshift({ text: oldTokens[i - 1], changed: true });
      i--;
    }
  }

  return { removed, added };
}

function computeLineDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const diff: DiffLine[] = [];

  let i = 0;
  let j = 0;
  let oldLineNo = 1;
  let newLineNo = 1;

  while (i < oldLines.length || j < newLines.length) {
    const oldLine = oldLines[i];
    const newLine = newLines[j];

    if (oldLine !== undefined && newLine !== undefined && oldLine === newLine) {
      diff.push({
        type: 'context',
        oldLineNo,
        newLineNo,
        text: oldLine,
      });
      i++;
      j++;
      oldLineNo++;
      newLineNo++;
      continue;
    }

    let matchInNew = -1;
    if (oldLine !== undefined) {
      for (
        let lookahead = 1;
        lookahead <= DIFF_LOOKAHEAD && j + lookahead < newLines.length;
        lookahead++
      ) {
        if (newLines[j + lookahead] === oldLine) {
          matchInNew = j + lookahead;
          break;
        }
      }
    }

    let matchInOld = -1;
    if (newLine !== undefined) {
      for (
        let lookahead = 1;
        lookahead <= DIFF_LOOKAHEAD && i + lookahead < oldLines.length;
        lookahead++
      ) {
        if (oldLines[i + lookahead] === newLine) {
          matchInOld = i + lookahead;
          break;
        }
      }
    }

    if (
      matchInNew !== -1 &&
      (matchInOld === -1 || matchInNew - j <= matchInOld - i)
    ) {
      while (j < matchInNew) {
        diff.push({
          type: 'add',
          newLineNo,
          text: newLines[j] ?? '',
        });
        j++;
        newLineNo++;
      }
      continue;
    }

    if (matchInOld !== -1) {
      while (i < matchInOld) {
        diff.push({
          type: 'del',
          oldLineNo,
          text: oldLines[i] ?? '',
        });
        i++;
        oldLineNo++;
      }
      continue;
    }

    if (oldLine !== undefined) {
      diff.push({
        type: 'del',
        oldLineNo,
        text: oldLine,
      });
      i++;
      oldLineNo++;
    }

    if (newLine !== undefined) {
      diff.push({
        type: 'add',
        newLineNo,
        text: newLine,
      });
      j++;
      newLineNo++;
    }
  }

  return diff;
}

function buildDiffHunks(lines: DiffLine[]): DiffHunk[] {
  const changedIndices = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => line.type !== 'context')
    .map(({ index }) => index);

  if (changedIndices.length === 0) {
    return [];
  }

  const ranges: Array<{ start: number; end: number }> = [];
  for (const changedIndex of changedIndices) {
    const start = Math.max(0, changedIndex - DIFF_CONTEXT_LINES);
    const end = Math.min(lines.length - 1, changedIndex + DIFF_CONTEXT_LINES);
    const last = ranges.at(-1);

    if (!last || start > last.end + 1) {
      ranges.push({ start, end });
    } else {
      last.end = Math.max(last.end, end);
    }
  }

  return ranges.map(({ start, end }) => {
    const hunkLines = lines.slice(start, end + 1);
    const oldLineNumbers = hunkLines
      .map(line => line.oldLineNo)
      .filter((v): v is number => v !== undefined);
    const newLineNumbers = hunkLines
      .map(line => line.newLineNo)
      .filter((v): v is number => v !== undefined);

    return {
      oldStart: oldLineNumbers[0] ?? 0,
      oldCount: oldLineNumbers.length,
      newStart: newLineNumbers[0] ?? 0,
      newCount: newLineNumbers.length,
      lines: hunkLines,
    };
  });
}

function renderWordParts(parts: WordPart[], className: string): ReactNode {
  return parts.map((part, index) => (
    <span
      key={`${part.text}-${index}`}
      className={part.changed ? className : undefined}
    >
      {part.text}
    </span>
  ));
}

type DocumentDiffViewerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previousContent: string;
  currentContent: string;
  rawComparison?: boolean;
};

export function DocumentDiffViewerModal({
  open,
  onOpenChange,
  previousContent,
  currentContent,
  rawComparison = false,
}: DocumentDiffViewerModalProps) {
  const [diffViewMode, setDiffViewMode] = useState<DiffViewMode>('git');

  const diffHunks = useMemo(() => {
    const lines = computeLineDiff(previousContent, currentContent);

    return buildDiffHunks(lines);
  }, [currentContent, previousContent]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='flex h-[85vh] w-full max-w-[1200px] flex-col overflow-hidden'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Last detected change comparison</Trans>
          </Dialog.Title>
          <Dialog.Description>
            {rawComparison ? (
              <Trans>
                Compare raw website content before and after the last
                significant change.
              </Trans>
            ) : (
              <Trans>
                Compare previously stored content with the current version.
              </Trans>
            )}
          </Dialog.Description>
        </Dialog.Header>

        <div className='mb-2 flex items-center justify-end gap-2'>
          <Button
            variant={diffViewMode === 'git' ? 'secondary' : 'ghost'}
            outline
            size='sm'
            onClick={() => setDiffViewMode('git')}
          >
            <Trans>Git diff</Trans>
          </Button>
          <Button
            variant={diffViewMode === 'raw' ? 'secondary' : 'ghost'}
            outline
            size='sm'
            onClick={() => setDiffViewMode('raw')}
          >
            <Trans>Raw side-by-side</Trans>
          </Button>
        </div>

        {diffViewMode === 'git' ? (
          <ScrollArea.Root className='min-h-0 flex-1'>
            <ScrollArea.Viewport className='size-full py-2'>
              <div className='space-y-4'>
                {diffHunks.length === 0 ? (
                  <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
                    <Trans>No line-level differences found.</Trans>
                  </div>
                ) : (
                  diffHunks.map((hunk, hunkIndex) => (
                    <div
                      key={`${hunk.oldStart}-${hunk.newStart}-${hunkIndex}`}
                      className='overflow-hidden rounded-md border border-border'
                    >
                      <div className='border-b border-border bg-muted/40 px-3 py-2 font-mono text-xs text-muted-fg'>
                        @@ -{hunk.oldStart},{hunk.oldCount} +{hunk.newStart},
                        {hunk.newCount} @@
                      </div>
                      <div className='font-mono text-xs leading-5'>
                        {hunk.lines.map((line, lineIndex) => {
                          if (
                            line.type === 'del' &&
                            hunk.lines[lineIndex + 1]?.type === 'add'
                          ) {
                            const addedLine = hunk.lines[lineIndex + 1];
                            const inline = diffWords(line.text, addedLine.text);

                            return (
                              <div key={`${hunkIndex}-${lineIndex}`}>
                                <div className='grid grid-cols-[64px_64px_20px_1fr] bg-danger/10'>
                                  <span className='px-2 text-right text-danger/70'>
                                    {line.oldLineNo}
                                  </span>
                                  <span className='px-2 text-right text-muted-fg/50'>
                                    &nbsp;
                                  </span>
                                  <span className='text-danger'>-</span>
                                  <span className='whitespace-pre-wrap pr-2 text-danger'>
                                    {renderWordParts(
                                      inline.removed,
                                      'rounded bg-danger/30',
                                    )}
                                  </span>
                                </div>
                                <div className='grid grid-cols-[64px_64px_20px_1fr] bg-success/10'>
                                  <span className='px-2 text-right text-muted-fg/50'>
                                    &nbsp;
                                  </span>
                                  <span className='px-2 text-right text-success/70'>
                                    {addedLine.newLineNo}
                                  </span>
                                  <span className='text-success'>+</span>
                                  <span className='whitespace-pre-wrap pr-2 text-success'>
                                    {renderWordParts(
                                      inline.added,
                                      'rounded bg-success/30',
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          }

                          if (
                            line.type === 'add' &&
                            hunk.lines[lineIndex - 1]?.type === 'del'
                          ) {
                            return null;
                          }

                          const prefix =
                            line.type === 'add'
                              ? '+'
                              : line.type === 'del'
                                ? '-'
                                : ' ';
                          const rowClass =
                            line.type === 'add'
                              ? 'bg-success/10'
                              : line.type === 'del'
                                ? 'bg-danger/10'
                                : 'bg-transparent';
                          const textClass =
                            line.type === 'add'
                              ? 'text-success'
                              : line.type === 'del'
                                ? 'text-danger'
                                : 'text-foreground';

                          return (
                            <div
                              key={`${hunkIndex}-${lineIndex}`}
                              className={`grid grid-cols-[64px_64px_20px_1fr] ${rowClass}`}
                            >
                              <span className='px-2 text-right text-muted-fg/70'>
                                {line.oldLineNo ?? ''}
                              </span>
                              <span className='px-2 text-right text-muted-fg/70'>
                                {line.newLineNo ?? ''}
                              </span>
                              <span
                                className={
                                  line.type === 'add'
                                    ? 'text-success'
                                    : line.type === 'del'
                                      ? 'text-danger'
                                      : 'text-muted-fg'
                                }
                              >
                                {prefix}
                              </span>
                              <span
                                className={`whitespace-pre-wrap pr-2 ${textClass}`}
                              >
                                {line.text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
              orientation='vertical'
            >
              <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        ) : (
          <div className='grid min-h-0 flex-1 grid-cols-2 gap-4 py-2'>
            <div className='min-h-0 rounded-md border border-border'>
              <div className='border-b border-border px-3 py-2 text-sm font-medium'>
                <Trans>Previous version</Trans>
              </div>
              <ScrollArea.Root className='h-full'>
                <ScrollArea.Viewport className='size-full p-3'>
                  <pre className='whitespace-pre-wrap text-xs leading-5'>
                    {previousContent}
                  </pre>
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar
                  className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                  orientation='vertical'
                >
                  <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
            </div>

            <div className='min-h-0 rounded-md border border-border'>
              <div className='border-b border-border px-3 py-2 text-sm font-medium'>
                <Trans>Current version</Trans>
              </div>
              <ScrollArea.Root className='h-full'>
                <ScrollArea.Viewport className='size-full p-3'>
                  <pre className='whitespace-pre-wrap text-xs leading-5'>
                    {currentContent}
                  </pre>
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar
                  className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                  orientation='vertical'
                >
                  <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
            </div>
          </div>
        )}

        <Dialog.Footer>
          <Button variant='secondary' onClick={() => onOpenChange(false)}>
            <Trans>Close</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
